import React from 'react';
import PropTypes from 'prop-types';
import Charts from './Charts';
import { renderTooltip } from '../../../util/QuickReportsUtils';

const BarChart = ({
  chartTitle,
  chartType,
  chartColor,
  xAxisLabel,
  yAxisLabel,
  plotLines,
  dataLabels,
  yAxisText,
  chartStacking,
  xLabel,
  yLabel,
  showLegend,
  labelFormatter,
  xLabelPadding,
  seriesStacking,
  dataLabelFormat,
  yLabelPadding,
  xAxisLabelDistance,
  dataLabelPosition,
  data,
  categories,
  clickEvent,
  legend,
  tickPlacement,
  yAxisLabelDistance,
  stackDataLabels,
  stackedChart,
  barChartWidth,
  barChartHeight,
  xAxisClick,
  legendItemClick,
}) => {
  const config = {
    title: {
      text: chartTitle,
      align: 'center',
      margin: 25,
      style: {
        fontSize: '16px',
        color: '#30435d',
      },
    },
    chart: {
      type: chartType,
      style: {
        fontFamily: 'Open Sans, sans-serif',
      },
      height: barChartHeight,
      width: barChartWidth,
    },
    legend,
    colors: chartColor,
    xAxis: {
      type: 'category',
      categories,
      tickmarkPlacement: tickPlacement,
      tickLength: 0,
      labels: {
        rotation: 0,
        // align: 'center',
        events: {
          click: xAxisClick,
        },
        x: xAxisLabelDistance,
        y: yAxisLabelDistance,
        style: {
          textOverflow: 'none',
          fontSize: '10px',
          color: '#30435d',
          textAlign: 'center',
        },
        enabled: xAxisLabel,
        format: xLabel,
        useHTML: true,
        padding: xLabelPadding,
        formatter() {
          return labelFormatter && labelFormatter[this.value]
            ? labelFormatter[this.value].toString()
            : this.value;
        },
      },
      gridLineColor: 'transparent',
    },
    yAxis: {
      title: {
        text: yAxisText,
      },
      labels: {
        enabled: yAxisLabel,
        format: yLabel,
        padding: yLabelPadding,
        rotation: 0,
        style: {
          fontSize: '8px',
          fontWeight: 'bold',
          color: '#30435d',
        },
      },
      plotLines,
      gridLineColor: '#bbc6d5',
    },
    tooltip: {
      useHTML: true,
      followPointer: true,
      headerFormat: '<b><span>{point.key}</span></b><br />',
      pointFormat: renderTooltip(data.tooltip),
      backgroundColor: '#fff',
      padding: '0',
      borderColor: 'RGB(71, 100, 138)',
      borderRadius: 6,
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick,
        },
        stacking: seriesStacking,
        point: {
          events: {
            click: clickEvent,
          },
        },
        dataLabels: {
          enabled: stackedChart ? stackDataLabels : true,
          style: {
            textOutline: false,
          },
          format: dataLabelFormat,
        },
      },
      column: {
        showInLegend: showLegend,
        stacking: chartStacking,
        pointPadding: 0.1,
        groupPadding: 0,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          color: 'white',
          format: dataLabelFormat,
        },
      },
    },
    series: stackedChart
      ? data.rawData
      : [
        {
          name: chartTitle,
          colorByPoint: chartColor.length > 1,
          dataLabels: {
            enabled: dataLabels,
            color: 'RGB(48, 67, 93)',
            format: dataLabelFormat,
            ...dataLabelPosition,
          },
          data: data.processedData,
        },
      ],
  };
  // console.log(" config data "+ JSON.stringify(config));
  return <Charts config={config} />;
};

BarChart.propTypes = {
  chartTitle: PropTypes.string,
  chartType: PropTypes.string,
  chartColor: PropTypes.arrayOf(PropTypes.string),
  xAxisLabel: PropTypes.bool,
  yAxisLabel: PropTypes.bool,
  data: PropTypes.shape({}),
  plotLines: PropTypes.arrayOf(PropTypes.shape({})),
  dataLabels: PropTypes.bool,
  stackDataLabels: PropTypes.bool,
  yAxisText: PropTypes.string,
  chartStacking: PropTypes.string,
  seriesStacking: PropTypes.string,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  xLabelPadding: PropTypes.number,
  yLabelPadding: PropTypes.number,
  showLegend: PropTypes.bool,
  stackedChart: PropTypes.bool,
  labelFormatter: PropTypes.shape({}),
  dataLabelFormat: PropTypes.string,
  xAxisLabelDistance: PropTypes.number,
  yAxisLabelDistance: PropTypes.number,
  barChartWidth: PropTypes.number,
  barChartHeight: PropTypes.number,
  dataLabelPosition: PropTypes.shape({}),
  clickEvent: PropTypes.func,
  xAxisClick: PropTypes.func,
  legendItemClick: PropTypes.func,
  legend: PropTypes.shape({}),
  tickPlacement: PropTypes.string,
};

BarChart.defaultProps = {
  chartTitle: '',
  chartType: 'column',
  chartColor: ['#e5cf8e'],
  xAxisLabel: true,
  yAxisLabel: true,
  dataLabels: false,
  plotLines: null,
  yAxisText: null,
  chartStacking: 'normal',
  seriesStacking: null,
  xLabel: '{value}',
  yLabel: '{value}',
  showLegend: false,
  xLabelPadding: 5,
  yLabelPadding: 5,
  dataLabelFormat: '{y}',
  xAxisLabelDistance: -10,
  yAxisLabelDistance: null,
  dataLabelPosition: {},
  barChartWidth: null,
  barChartHeight: null,
  clickEvent: () => {},
  xAxisClick: () => {},
  legend: {
    enabled: false,
  },
  stackDataLabels: false,
  tickPlacement: 'between',
  stackedChart: false,
  legendItemClick: undefined,
};

export default BarChart;
